import { type Space, bytesToNDigits } from '@mail/cross-sizes-utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    personalizationBusinessTariffs,
    personalizationMinimalTariffs,
    PersonalizationPeriod,
    PersonalizationQuota,
} from 'reactApp/appHelpers/featuresHelpers/features/personalization';
import { BillingDataSelectors } from 'reactApp/modules/billingData/billingData.selectors';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { BillingPersonalizationSelectors } from 'reactApp/modules/billingPersonalization/billingPersonalization.selectors';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { loadProductsList } from 'reactApp/modules/productsList/productsList.module';
import { ProductsListSelectors } from 'reactApp/modules/productsList/productsList.selectors';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import type { RootState } from 'reactApp/store';
import { isYear1Period } from 'reactApp/utils/Period';

interface UseCurrentSubscriptionResult {
    subscription: BillingDataSubscriptionInfo | undefined;
    space: Space | undefined;
}

interface UsePredictedSubscriptionResult {
    tariffId: string | null;
    space: Space | undefined;
}

interface UsePersonalizationPromoInformationResult {
    currentSpace: Space | undefined;
    promoSpace: Space | undefined;
    handleBuy: () => void;
    showPromo: boolean;
}

const SPACE_TO_QUOTA: Record<number, PersonalizationQuota> = {
    256: PersonalizationQuota.W256GB,
    512: PersonalizationQuota.W512GB,
    1024: PersonalizationQuota.W1TB,
    2048: PersonalizationQuota.W2TB,
    4096: PersonalizationQuota.W4TB,
};

/** Извлекает размер квоты в GB из названия тарифа */
const extractQuotaFromTariffId = (tariffId: string): number | null => {
    const gbMatch = tariffId.match(/W(\d+)GB/i);
    if (gbMatch) {
        return Number(gbMatch[1]);
    }

    const tbMatch = tariffId.match(/W(\d+)TB/i);
    if (tbMatch) {
        return Number(tbMatch[1]) * 1024;
    }

    return null;
};

const getPersonalizationTariffId = (spaceGb: number, isBusiness: boolean, period: PersonalizationPeriod): string | undefined => {
    const quota: PersonalizationQuota = SPACE_TO_QUOTA[spaceGb];
    if (!quota) {
        return;
    }

    const tariffs = isBusiness ? personalizationBusinessTariffs : personalizationMinimalTariffs;
    return tariffs?.[period]?.[quota];
};

const isBusinessProduct = (productId: string): boolean => {
    return productId.toLowerCase().includes('business');
};

export const useCurrentSubscription = (): UseCurrentSubscriptionResult => {
    const subscriptions = useSelector(BillingDataSelectors.sortSubscriptionsBySpace);
    const subscription = subscriptions?.[subscriptions.length - 1];

    return {
        subscription,
        space: subscription?.space,
    };
};

const GB_TO_BYTES = 1024 * 1024 * 1024;
const MAX_DIGITS = 4;

let pendingPaymentTariffId: string | null = null;

/** Хук отдает подписку которую предсказала ML модель */
export const usePredictedSubscription = (): UsePredictedSubscriptionResult => {
    const { subscription: currentSubscription, space: currentSpace } = useCurrentSubscription();
    const tariffs = useSelector(BillingPersonalizationSelectors.getTariffsWebEntryPoints);

    const isYearPeriod = isYear1Period(currentSubscription?.productPeriod || '');
    const tariffIds = isYearPeriod ? tariffs?.year : tariffs?.month;
    const currentSpaceGb = Math.round((currentSpace?.original ?? 0) / GB_TO_BYTES);

    const predictedTariffId = useMemo(() => {
        if (!tariffIds?.length) {
            return null;
        }

        const nextTariff = tariffIds
            .map((id) => ({ id, spaceGb: extractQuotaFromTariffId(id) }))
            .find((t) => t.spaceGb !== null && t.spaceGb > currentSpaceGb);

        return nextTariff?.id ?? null;
    }, [tariffIds, currentSpaceGb]);

    const featureTariffId = useMemo(() => {
        if (!predictedTariffId) {
            return null;
        }

        const spaceGb = extractQuotaFromTariffId(predictedTariffId);
        if (!spaceGb) {
            return null;
        }

        const isBusiness = isBusinessProduct(predictedTariffId);
        const period = isYearPeriod ? PersonalizationPeriod.Year : PersonalizationPeriod.Month;
        return getPersonalizationTariffId(spaceGb, isBusiness, period) ?? null;
    }, [predictedTariffId, isYearPeriod]);

    const space = useMemo(() => {
        if (!predictedTariffId) {
            return undefined;
        }

        const spaceGb = extractQuotaFromTariffId(predictedTariffId);
        if (!spaceGb) {
            return undefined;
        }

        const bytes = spaceGb * GB_TO_BYTES;
        return bytesToNDigits(bytes, MAX_DIGITS);
    }, [predictedTariffId]);

    return {
        tariffId: featureTariffId,
        space,
    };
};

/** Хук отдает информацю о подписке из ML модели для UI
 * @returns {Object} Объект с данными о подписке
 * @returns {Space} returns.currentSpace - Текущий размер квоты у пользователя
 * @returns {Space} returns.promoSpace - Предлагаемая квота пользователю в промо
 * @returns {() => void} returns.handleBuy - Функция покупки подписки из ML модели
 * @returns {boolean} returns.showPromo - Показываем акцию или нет, сделано, чтобы после попкупки скрывать блоки */
export const usePersonalizationPromoInformation = (): UsePersonalizationPromoInformationResult => {
    const dispatch = useDispatch();
    const { space: currentSpace } = useCurrentSubscription();
    const { tariffId, space: promoSpace } = usePredictedSubscription();
    const isMobile = useSelector(QuotaLandingSelectors.isMobile);
    const [showPromo] = useState<boolean>(true);
    const prevProductFromListRef = useRef<typeof productFromList>(undefined);

    const productFromList = useSelector((state: RootState) =>
        tariffId ? ProductsListSelectors.getProductById(state, tariffId) : undefined
    );

    const productJustLoaded = prevProductFromListRef.current === undefined && productFromList !== undefined;
    prevProductFromListRef.current = productFromList;

    useEffect(() => {
        if (!productJustLoaded || pendingPaymentTariffId !== tariffId || !tariffId) {
            return;
        }

        pendingPaymentTariffId = null;

        let purchaseSucceeded = false;

        dispatch(
            openTariffBuy({
                productId: tariffId,
                checkAnonymous: true,
                isMobile,
                paySource: getPaySource('personalization_modal'),
                hideOtherTariffs: true,
                onSuccess: () => {
                    purchaseSucceeded = true;
                },
                onClose: () => {
                    if (purchaseSucceeded) {
                        setTimeout(() => {
                            window.location.reload();
                        }, 2000);
                    }
                },
            })
        );
    }, [productFromList, productJustLoaded, tariffId, isMobile]);

    const handleBuy = useCallback(() => {
        if (!tariffId) {
            return;
        }

        if (productFromList) {
            let purchaseSucceeded = false;

            dispatch(
                openTariffBuy({
                    productId: tariffId,
                    checkAnonymous: true,
                    isMobile,
                    paySource: getPaySource('personalization_modal'),
                    hideOtherTariffs: true,
                    onSuccess: () => {
                        purchaseSucceeded = true;
                    },
                    onClose: () => {
                        if (purchaseSucceeded) {
                            setTimeout(() => {
                                window.location.reload();
                            }, 2000);
                        }
                    },
                })
            );
            return;
        }

        dispatch(loadProductsList([tariffId]));
        pendingPaymentTariffId = tariffId;
    }, [dispatch, tariffId, isMobile, productFromList]);

    return { currentSpace, promoSpace, handleBuy, showPromo };
};
